import { useMemo, useState } from "react";
import type { Drama } from "@/mocks/dramas";
import DramaCard from "./DramaCard";
import EmptyState from "./EmptyState";

interface DramaListViewProps {
  dramas: Drama[];
  emptyTitle?: string;
  emptyDescription?: string;
}

const PAGE_SIZE = 8;

const sortOptions = [
  { value: "latest", label: "最新更新" },
  { value: "hot", label: "最热人气" },
  { value: "rating", label: "评分最高" },
  { value: "comments", label: "评论最多" },
];

const ratingOptions = [
  { value: "all", label: "全部评分" },
  { value: "8.5", label: "8.5 分以上" },
  { value: "9", label: "9 分以上" },
];

export default function DramaListView({
  dramas,
  emptyTitle = "没有找到匹配的剧集",
  emptyDescription = "试试调整筛选条件，或重置后重新查看全部作品。",
}: DramaListViewProps) {
  const [sort, setSort] = useState("latest");
  const [year, setYear] = useState("all");
  const [tag, setTag] = useState("all");
  const [minRating, setMinRating] = useState("all");
  const [page, setPage] = useState(1);

  const years = useMemo(
    () => Array.from(new Set(dramas.map((d) => d.year))).sort((a, b) => b - a),
    [dramas],
  );
  const allTags = useMemo(
    () => Array.from(new Set(dramas.flatMap((d) => d.tags))),
    [dramas],
  );

  const filtered = useMemo(() => {
    const list = dramas.filter(
      (d) =>
        (year === "all" || d.year === Number(year)) &&
        (tag === "all" || d.tags.includes(tag)) &&
        (minRating === "all" || d.rating >= Number(minRating)),
    );
    return [...list].sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "comments") return b.comments - a.comments;
      return b.updateDate.localeCompare(a.updateDate);
    });
  }, [dramas, sort, year, tag, minRating]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const changeFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const reset = () => {
    setSort("latest");
    setYear("all");
    setTag("all");
    setMinRating("all");
    setPage(1);
  };

  const selectClass =
    "cursor-pointer rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-700 outline-none transition-colors hover:border-primary-300 focus:border-primary-400";

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-500 whitespace-nowrap">排序</span>
          <select value={sort} onChange={(e) => changeFilter(setSort)(e.target.value)} className={selectClass}>
            {sortOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-500 whitespace-nowrap">年份</span>
          <select value={year} onChange={(e) => changeFilter(setYear)(e.target.value)} className={selectClass}>
            <option value="all">全部年份</option>
            {years.map((y) => (
              <option key={y} value={String(y)}>
                {y} 年
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-500 whitespace-nowrap">标签</span>
          <select value={tag} onChange={(e) => changeFilter(setTag)(e.target.value)} className={selectClass}>
            <option value="all">全部标签</option>
            {allTags.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-500 whitespace-nowrap">评分</span>
          <select
            value={minRating}
            onChange={(e) => changeFilter(setMinRating)(e.target.value)}
            className={selectClass}
          >
            {ratingOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={reset}
          className="ml-auto flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
        >
          <i className="ri-refresh-line"></i>重置筛选
        </button>
      </div>

      <p className="mt-4 text-sm text-foreground-500">
        共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 部作品
      </p>

      {pageItems.length === 0 ? (
        <div className="mt-5">
          <EmptyState title={emptyTitle} description={emptyDescription} actionLabel="重置筛选" onAction={reset} />
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {pageItems.map((drama, i) => (
              <DramaCard key={drama.id} drama={drama} index={i} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i + 1)}
                  className={`h-9 min-w-9 rounded-md px-2 text-sm transition-colors cursor-pointer ${
                    currentPage === i + 1
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}